<template>
  <nav class="w-full border-b border-solid border-sienna-muted">
    <NavBarCheckout v-if="isOnCheckout" />
    <NavBarMyAccount
      v-else
      :is-logged-in="isLoggedIn"
      :is-on-login-or-signup="isOnLoginOrSignUp"
      :active-icon="Boolean(isOnMyAccount)"
      :hide-shop-now="isCatering || isOnEvents || isOnRestaurantDetails"
      @open-mobile="isMobileMenuOpen = true"
      @logout="logout"
    />
  </nav>
  <NavBarMobile
    v-model="isMobileMenuOpen"
    :is-logged-in="isLoggedIn"
    :show-my-account-links="!isOnProductBox && !isOnCheckout"
    @open-modal="openModal"
    @logout="logout"
  />
</template>

<script lang="ts" setup>
  import { computed, nextTick, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import useCurrentCustomer from '@/stores/currentCustomerStore';
  import usePreventScroll from '@/composables/usePreventScroll';
  import * as Sentry from '@sentry/vue';
  import useLogout from '@/api/mutations/pensa/logout';

  const emit = defineEmits<{ (e: 'openModal'): void }>();

  const customerStore = useCurrentCustomer();
  const router = useRouter();
  const isMobileMenuOpen = ref(false);
  const logoutMutation = useLogout();

  usePreventScroll(isMobileMenuOpen);

  const isOnCheckout = computed(
    () =>
      router.currentRoute.value.name &&
      typeof router.currentRoute.value.name === 'string' &&
      [
        'checkout-login',
        'checkout-signup',
        'checkout-billing-address',
        'checkout-payment',
        'delivery-address',
        'delivery-instructions',
        'checkout-social-login',
        'pasta-now-checkout-login',
        'pasta-now-checkout-social-login',
        'pasta-now-checkout-signup',
        'pasta-now-delivery-address',
        'pasta-now-delivery-instructions',
        'pasta-now-payment',
        'catering-checkout-social-login',
        'catering-checkout-login',
        'catering-checkout-signup',
        'catering-delivery-address',
        'catering-delivery-instructions',
        'catering-payment',
      ].includes(router.currentRoute.value.name)
  );

  const isOnProductBox = computed(() => router.currentRoute.value.name === 'product-box');

  const isOnEvents = computed(() =>
    Boolean(
      router.currentRoute.value.name &&
        ((router.currentRoute.value.name as string).startsWith('events') ||
          (router.currentRoute.value.name as string).startsWith('private-events'))
    )
  );

  const isOnMyAccount = computed(
    () =>
      router.currentRoute.value.name &&
      typeof router.currentRoute.value.name === 'string' &&
      [
        'upcoming-orders',
        'my-account-dashboard',
        'order-history',
        'rewards',
        'details',
        'pasta-plan',
        'restaurants',
        'restaurant-details',
      ].includes(router.currentRoute.value.name)
  );

  const isCatering = computed(() =>
    Boolean(
      router.currentRoute.value.name &&
        (router.currentRoute.value.name as string).startsWith('catering')
    )
  );

  const isOnRestaurantDetails = computed(
    () =>
      Boolean(
        router.currentRoute.value.name &&
          (router.currentRoute.value.name as string).startsWith('restaurant-details')
      ) || router.currentRoute.value.name === 'restaurants'
  );

  const isLoggedIn = computed(() => Boolean(customerStore.email));

  const isOnLoginOrSignUp = computed(() =>
    Boolean(
      router.currentRoute.value.name &&
        typeof router.currentRoute.value.name === 'string' &&
        ['login', 'signup', 'social-login'].includes(router.currentRoute.value.name)
    )
  );

  watch(
    () => customerStore.email,
    () => {
      if (customerStore.email) {
        Sentry.setUser({ email: customerStore.email });
      }
    },
    { immediate: true }
  );

  const logout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        isMobileMenuOpen.value = false;
        if (import.meta.env.VITE_IS_PROD) {
          window.location.replace('https://pastaevangelists.com/account/logout');
        }
      },
    });
  };
  const openModal = () => {
    isMobileMenuOpen.value = false;
    nextTick(() => emit('openModal'));
  };
</script>
